import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import { registerDynamicCallback } from './servdUtils.js';

/**
 * Check if PhotoSwipe is available
 */
function isPhotoSwipeAvailable() {
  return typeof PhotoSwipeLightbox !== 'undefined' && PhotoSwipeLightbox;
}

/**
 * Initialize PhotoSwipe galleries
 */
export function initGallery() {
  // Check if PhotoSwipe is available
  if (!isPhotoSwipeAvailable()) {
    console.error('PhotoSwipe is not available. Please check the import.');
    return;
  }
  
  try {
    // Handle regular galleries
    const galleries = document.querySelectorAll('.gallery');
    
    galleries.forEach(gallery => {
      try {
        const lightbox = new PhotoSwipeLightbox({
          gallery: gallery,
          children: 'a:not(.show-popup-gallery)',
          pswpModule: () => import('photoswipe')
        });
        
        // Add caption support for regular galleries
        lightbox.on('uiRegister', function() {
          lightbox.pswp.ui.registerElement({
            name: 'custom-caption',
            order: 9,
            isButton: false,
            appendTo: 'root',
            html: 'Caption text',
            onInit: (el, pswp) => {
              pswp.on('change', () => {
                const currSlideElement = pswp.currSlide.data.element;
                let captionHTML = '';
                
                if (currSlideElement) {
                  const hiddenCaption = currSlideElement.querySelector('.hidden-caption-content');
                  if (hiddenCaption && hiddenCaption.innerHTML.trim()) {
                    captionHTML = hiddenCaption.innerHTML;
                  }
                }
                
                // Update caption content and visibility
                if (captionHTML) {
                  el.innerHTML = captionHTML;
                  el.classList.remove('pswp__custom-caption--hidden');
                } else {
                  el.innerHTML = '';
                  el.classList.add('pswp__custom-caption--hidden');
                }
              });
            }
          });
        });
        
        lightbox.init();
        
        // Handle click events on gallery images
        gallery.addEventListener('click', (e) => {
          if (e.target.tagName === 'IMG') {
            e.preventDefault();
            const index = Array.from(gallery.querySelectorAll('a:not(.show-popup-gallery)')).indexOf(e.target.parentElement);
            lightbox.loadAndOpen(index);
          }
        });
      } catch (error) {
        console.error('Error initializing regular gallery:', error);
      }
    });
    
    // Handle popup galleries - basic PhotoSwipe setup
    const popupGalleries = document.querySelectorAll('.popup-gallery');
    
    popupGalleries.forEach((gallery, index) => {
      try {
        // Ensure each gallery has a unique ID
        if (!gallery.id || gallery.id === 'popup-gallery') {
          gallery.id = `popup-gallery-${Date.now()}-${index}`;
        }
        
        const lightbox = new PhotoSwipeLightbox({
          gallery: '#' + gallery.id,
          children: 'a:not(.show-popup-gallery)',
          pswpModule: () => import('photoswipe')
        });
        
        // Add caption support for popup galleries
        lightbox.on('uiRegister', function() {
          lightbox.pswp.ui.registerElement({
            name: 'custom-caption',
            order: 9,
            isButton: false,
            appendTo: 'root',
            html: 'Caption text',
            onInit: (el, pswp) => {
              pswp.on('change', () => {
                const currSlideElement = pswp.currSlide.data.element;
                let captionHTML = '';
                
                if (currSlideElement) {
                  const hiddenCaption = currSlideElement.querySelector('.hidden-caption-content');
                  if (hiddenCaption && hiddenCaption.innerHTML.trim()) {
                    captionHTML = hiddenCaption.innerHTML;
                  }
                }
                
                // Update caption content and visibility
                if (captionHTML) {
                  el.innerHTML = captionHTML;
                  el.classList.remove('pswp__custom-caption--hidden');
                } else {
                  el.innerHTML = '';
                  el.classList.add('pswp__custom-caption--hidden');
                }
              });
            }
          });
        });
        
        lightbox.init();
        
        // Handle "show all images" button
        const showButton = gallery.querySelector('.show-popup-gallery');
        if (showButton) {
          showButton.addEventListener('click', (e) => {
            e.preventDefault();
            lightbox.loadAndOpen(0);
          });
        }
      } catch (error) {
        console.error('Error initializing popup gallery:', gallery.id, error);
      }
    });
  } catch (error) {
    console.error('Error in initGallery:', error);
  }
}

/**
 * Initialize galleries and register with Servd utilities
 */
export function initGalleryWithServd() {
  // Register the gallery function to be re-run when dynamic content loads
  registerDynamicCallback(initGallery, 'gallery');
  
  // Initial gallery setup
  initGallery();
  
  // Add a listener for Servd dynamic content loading using the correct event name
  window.addEventListener('servd.dynamicloaded', function(e) {
    // Check if galleries exist now
    const popupGalleries = document.querySelectorAll('.popup-gallery');
    
    if (popupGalleries.length > 0) {
      initGallery();
    }
  });
} 